import { computeTrend, type TrendDirection } from '../lib/timeBuckets';

// Dependency-free inline SVG sparkline. The app has zero charting/visualization dependencies
// today (no MUI, no icon library, no chart lib) and a KPI trend line is a single polyline —
// not enough to justify pulling in a charting library's bundle weight for one shape.
export function Sparkline({ values, width = 72, height = 22 }: { values: number[]; width?: number; height?: number }) {
  if (values.length < 2) return null;

  const max = Math.max(...values);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const stepX = width / (values.length - 1);
  const points = values
    .map((value, index) => `${(index * stepX).toFixed(1)},${(height - ((value - min) / range) * height).toFixed(1)}`)
    .join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="sparkline" role="img" aria-label="Trend over recent periods">
      <polyline points={points} fill="none" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const ARROW: Record<TrendDirection, string> = { up: '↑', down: '↓', flat: '→' };

// A KPI where higher is better (e.g. throughput) should color an upward trend as good; a KPI
// where lower is better (e.g. failures) should color it as bad. `goodDirection: 'neutral'`
// skips the good/bad judgment entirely (e.g. raw volume, where more isn't inherently good or
// bad) and just shows the direction.
export type TrendGoodDirection = 'up' | 'down' | 'neutral';

function trendTone(direction: TrendDirection, goodDirection: TrendGoodDirection): 'good' | 'bad' | 'neutral' {
  if (direction === 'flat' || goodDirection === 'neutral') return 'neutral';
  return direction === goodDirection ? 'good' : 'bad';
}

export function KpiTrend({ buckets, goodDirection, periodLabel }: {
  buckets: number[];
  goodDirection: TrendGoodDirection;
  periodLabel: string;
}) {
  const trend = computeTrend(buckets);
  const tone = trendTone(trend.direction, goodDirection);
  const changeLabel = trend.direction === 'flat'
    ? 'steady'
    : trend.changePercent === null
      ? 'new activity'
      : `${trend.changePercent > 0 ? '+' : ''}${Math.round(trend.changePercent)}%`;

  return (
    <div className={`kpi-trend kpi-trend-${tone}`}>
      <Sparkline values={buckets} />
      <span className="kpi-trend-label">
        {ARROW[trend.direction]} {changeLabel} <span className="kpi-trend-period">vs. {periodLabel}</span>
      </span>
    </div>
  );
}
